import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, ArrowRight, Cog, Map as MapIcon, Users, Compass,
  Trophy, CheckCircle2, Clock, Target, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../api';
import { useApp } from '../contexts/AppContext';

// Méta des 4 compétences officielles REMC — Catégorie A.
// Source : référentiel /app/frontend/src/data/livretApprentissageA.js
const COMPETENCES = [
  {
    code: 'C1',
    label: 'Maîtriser',
    sub: 'Le maniement de la moto',
    total: 12,
    color: '#0891b2',
    bg: 'rgba(8, 145, 178, 0.08)',
    icon: Cog,
  },
  {
    code: 'C2',
    label: 'Appréhender',
    sub: 'La route et la circulation',
    total: 8,
    color: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.08)',
    icon: MapIcon,
  },
  {
    code: 'C3',
    label: 'Circuler',
    sub: 'En conditions difficiles',
    total: 11,
    color: '#dc2626',
    bg: 'rgba(220, 38, 38, 0.08)',
    icon: Users,
  },
  {
    code: 'C4',
    label: 'Pratiquer',
    sub: 'Une conduite autonome',
    total: 7,
    color: '#16a34a',
    bg: 'rgba(22, 163, 74, 0.08)',
    icon: Compass,
  },
];

const TOTAL_SUB = 38;

/**
 * Section "Mon Livret d'Apprentissage" — Home page.
 *
 * Bloc pédagogique présentant le référentiel officiel REMC A et permettant
 * à l'élève de visualiser sa progression sur les 38 sous-compétences.
 *
 * Deux modes :
 *  - Utilisateur connecté → données réelles via /api/livret/progression
 *  - Visiteur → teaser avec 4 cartes compétences + CTA inscription
 */
export default function HomeLivretSection() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const isLogged = !!user;

  useEffect(() => {
    if (!isLogged) {
      setSummary(null);
      return;
    }
    let active = true;
    setLoading(true);
    api.getLivretProgression()
      .then((data) => {
        if (active) setSummary(data?.summary || null);
      })
      .catch(() => { if (active) setSummary(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isLogged]);

  const totalAcquis = summary?.acquis ?? 0;
  const totalEnCours = summary?.en_cours ?? 0;
  const globalPct = summary?.percent ?? 0;
  const badges = summary?.badges ?? [];
  const byComp = summary?.by_competence || {};

  const handleCTA = () => {
    if (isLogged) {
      navigate('/mon-livret-moto');
    } else {
      // Page gate /mon-livret-moto qui redirige vers /login si pas connecté
      navigate('/mon-livret-moto');
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-2xl bg-card"
      data-testid="home-livret"
      aria-labelledby="livret-title"
      style={{
        background:
          'radial-gradient(ellipse 70% 50% at 100% 0%, rgba(8, 145, 178, 0.06), transparent 65%), radial-gradient(ellipse 70% 50% at 0% 100%, rgba(22, 163, 74, 0.05), transparent 65%), hsl(var(--card))',
      }}
    >
      {/* Pattern subtil */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(-45deg, transparent 0 12px, hsl(var(--foreground)) 12px 13px)',
        }}
      />

      <div className="relative px-5 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12">
        {/* Header */}
        <div className="flex items-end justify-between mb-5 sm:mb-7 gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Pédagogique · REMC officiel
              </span>
            </div>
            <h2
              id="livret-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground inline-flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--primary))] flex-shrink-0" />
              Mon Livret d'Apprentissage
            </h2>
            <p className="text-xs sm:text-sm text-foreground/60 mt-2 max-w-xl">
              {isLogged
                ? "Auto-évalue-toi sur les 38 sous-compétences officielles du permis A. Ta progression est sauvegardée."
                : "Le référentiel officiel des 38 compétences à maîtriser pour le permis A. Suis ta progression et auto-évalue-toi gratuitement."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCTA}
            className="text-[11px] sm:text-xs font-bold text-foreground/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-1 whitespace-nowrap flex-shrink-0 hide-in-oral"
            data-testid="livret-section-see-all"
          >
            Voir tout <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Stat hero — uniquement si connecté */}
        {isLogged && (
          <div
            className="mb-5 sm:mb-7 p-4 sm:p-5 rounded-xl border bg-card/40"
            data-testid="livret-section-progress-summary"
          >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <div className="text-3xl sm:text-4xl font-black tabular-nums leading-none">
                  {totalAcquis}
                  <span className="text-lg sm:text-xl text-foreground/50 font-bold">/{TOTAL_SUB}</span>
                </div>
                <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-foreground/60 font-bold mt-1">
                  compétences acquises
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl sm:text-3xl font-black tabular-nums" style={{ color: '#16a34a' }}>
                  {Math.round(globalPct)}%
                </div>
                <div className="text-[10px] text-foreground/55 flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" /> {totalEnCours} en cours
                </div>
              </div>
            </div>

            {/* Barre de progression globale */}
            <div className="h-2 rounded-full overflow-hidden bg-foreground/10">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${globalPct}%`,
                  background: 'linear-gradient(90deg, #0891b2 0%, #7c3aed 33%, #dc2626 66%, #16a34a 100%)',
                }}
              />
            </div>

            {/* Badges */}
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {badges.map((b) => {
                  const comp = COMPETENCES.find((c) => c.code === b);
                  if (!comp) return null;
                  return (
                    <Badge
                      key={b}
                      className="text-[10px] gap-1 px-2 py-0.5 border-none"
                      style={{ backgroundColor: comp.color, color: '#fff' }}
                      data-testid={`livret-section-badge-${b}`}
                    >
                      <Trophy className="w-3 h-3" /> {b} maîtrisée
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Grille 4 compétences */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 mb-5 sm:mb-7"
          data-testid="livret-section-grid"
        >
          {COMPETENCES.map((c) => {
            const Icon = c.icon;
            const stats = byComp[c.code] || { acquis: 0, en_cours: 0, total: c.total };
            const pct = (stats.acquis / c.total) * 100;
            const isComplete = stats.acquis >= c.total;
            return (
              <button
                key={c.code}
                type="button"
                onClick={handleCTA}
                className="group text-left p-3 sm:p-4 rounded-xl border transition-all hover:border-foreground/30 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2"
                style={{
                  background: c.bg,
                  borderColor: isComplete ? c.color : 'hsl(var(--border))',
                }}
                data-testid={`livret-section-card-${c.code}`}
                aria-label={`${c.code} · ${c.label} · ${stats.acquis}/${c.total} acquises`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: c.color }}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <span
                    className="font-black text-sm sm:text-base"
                    style={{ color: c.color }}
                  >
                    {c.code}
                  </span>
                  {isComplete && (
                    <CheckCircle2 className="w-4 h-4 ml-auto" style={{ color: c.color }} />
                  )}
                </div>

                <div className="text-[11px] sm:text-xs font-bold uppercase tracking-wide text-foreground/90 leading-tight">
                  {c.label}
                </div>
                <div className="text-[10px] sm:text-[11px] text-foreground/55 mt-0.5 leading-snug line-clamp-2">
                  {c.sub}
                </div>

                {isLogged ? (
                  <div className="mt-2.5 space-y-1">
                    <div className="text-[10px] tabular-nums font-bold text-foreground/70">
                      {stats.acquis}/{c.total} acquises
                    </div>
                    <div className="h-1 rounded-full overflow-hidden bg-foreground/10">
                      <div
                        className="h-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: c.color }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mt-2.5 text-[10px] tabular-nums font-bold text-foreground/70">
                    {c.total} sous-compétences
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* CTA principal */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
          <Button
            size="lg"
            onClick={handleCTA}
            className="h-11 sm:h-12 px-5 sm:px-6 rounded-full font-bold text-sm sm:text-base group flex-1 sm:flex-initial"
            data-testid="livret-section-main-cta"
          >
            {isLogged ? (
              <>
                <Target className="w-4 h-4 mr-2" />
                Continuer mon livret
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Découvrir mon livret
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </Button>

          {!isLogged && (
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/register')}
              className="h-11 sm:h-12 px-5 sm:px-6 rounded-full font-bold text-sm sm:text-base"
              data-testid="livret-section-register-cta"
            >
              Créer un compte gratuit
            </Button>
          )}

          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-foreground/55 ml-auto">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#16a34a]" />
            <span>100 % gratuit · sauvegardé sur ton compte</span>
          </div>
        </div>

        {/* Mention 100% gratuit mobile (sous CTA) */}
        <div className="sm:hidden flex items-center justify-center gap-1.5 mt-3 text-[10px] text-foreground/55">
          <CheckCircle2 className="w-3 h-3 text-[#16a34a]" />
          <span>100 % gratuit · sauvegardé sur ton compte</span>
        </div>

        {/* Loading discret */}
        {loading && isLogged && !summary && (
          <div className="absolute top-3 right-3 text-[10px] text-foreground/40">
            Chargement…
          </div>
        )}
      </div>
    </section>
  );
}
